import type { WorkspaceStore } from "./store";
/**
 * Undo / redo via the Command pattern.
 *
 * Each user-level mutation is wrapped in a `Command` with `apply()` and
 * `revert()` methods. The UndoStack collects these and exposes `undo()` /
 * `redo()`. A new `apply()` after `undo()` truncates the redo stack — the
 * same model as a code editor.
 *
 * Commands are responsible for capturing the "before" state when constructed
 * so `revert()` can restore it. Helpers below build commands for the common
 * mutations on a WorkspaceStore.
 */
import type { Concept, Diagram, Relation } from "./types";

export interface Command {
  /** Human-readable label, used in UI ("Undo: rename concept"). */
  label: string;
  apply(): void;
  revert(): void;
}

export class UndoStack {
  private readonly undoStack: Command[] = [];
  private readonly redoStack: Command[] = [];
  private readonly limit: number;

  constructor(limit = 200) {
    this.limit = limit;
  }

  apply(command: Command): void {
    command.apply();
    this.undoStack.push(command);
    if (this.undoStack.length > this.limit) {
      this.undoStack.shift();
    }
    this.redoStack.length = 0;
  }

  undo(): boolean {
    const command = this.undoStack.pop();
    if (!command) {
      return false;
    }
    command.revert();
    this.redoStack.push(command);
    return true;
  }

  redo(): boolean {
    const command = this.redoStack.pop();
    if (!command) {
      return false;
    }
    command.apply();
    this.undoStack.push(command);
    return true;
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  peekUndo(): Command | undefined {
    return this.undoStack[this.undoStack.length - 1];
  }

  peekRedo(): Command | undefined {
    return this.redoStack[this.redoStack.length - 1];
  }

  clear(): void {
    this.undoStack.length = 0;
    this.redoStack.length = 0;
  }
}

// ---- Command builders -----------------------------------------------------

export function upsertConceptCommand(store: WorkspaceStore, next: Concept): Command {
  const previous = store.getConcept(next.id);
  return {
    label: previous
      ? `Update concept '${labelOf(next.name)}'`
      : `Add concept '${labelOf(next.name)}'`,
    apply: () => {
      store.transaction(() => store.upsertConcept(next));
    },
    revert: () => {
      store.transaction(() => {
        if (previous) {
          store.upsertConcept(previous);
        } else {
          store.removeConcept(next.id);
        }
      });
    },
  };
}

export function removeConceptCommand(store: WorkspaceStore, id: string): Command {
  const previous = store.getConcept(id);
  if (!previous) {
    return noop("Remove concept (not found)");
  }
  // Capture relations that will cascade-delete.
  const relatedRelations = store
    .listRelations()
    .filter((r) => r.sourceConceptId === id || r.targetConceptId === id);
  return {
    label: `Remove concept '${labelOf(previous.name)}'`,
    apply: () => {
      store.transaction(() => store.removeConcept(id));
    },
    revert: () => {
      store.transaction(() => {
        store.upsertConcept(previous);
        for (const r of relatedRelations) {
          store.upsertRelation(r);
        }
      });
    },
  };
}

export function upsertRelationCommand(store: WorkspaceStore, next: Relation): Command {
  const previous = store.getRelation(next.id);
  return {
    label: previous ? "Update relation" : "Add relation",
    apply: () => {
      store.transaction(() => store.upsertRelation(next));
    },
    revert: () => {
      store.transaction(() => {
        if (previous) {
          store.upsertRelation(previous);
        } else {
          store.removeRelation(next.id);
        }
      });
    },
  };
}

export function removeRelationCommand(store: WorkspaceStore, id: string): Command {
  const previous = store.getRelation(id);
  if (!previous) {
    return noop("Remove relation (not found)");
  }
  return {
    label: "Remove relation",
    apply: () => {
      store.transaction(() => store.removeRelation(id));
    },
    revert: () => {
      store.transaction(() => store.upsertRelation(previous));
    },
  };
}

export function upsertDiagramCommand(store: WorkspaceStore, next: Diagram): Command {
  const previous = store.getDiagram(next.id);
  return {
    label: previous
      ? `Update diagram '${labelOf(next.name)}'`
      : `Add diagram '${labelOf(next.name)}'`,
    apply: () => {
      store.transaction(() => store.upsertDiagram(next));
    },
    revert: () => {
      store.transaction(() => {
        if (previous) {
          store.upsertDiagram(previous);
        } else {
          store.removeDiagram(next.id);
        }
      });
    },
  };
}

// ---- Helpers --------------------------------------------------------------

function labelOf(bilingual: Record<string, string>): string {
  // Prefer NL, then EN, then anything; never returns empty.
  return bilingual.nl ?? bilingual.en ?? Object.values(bilingual)[0] ?? "(unnamed)";
}

function noop(label: string): Command {
  return {
    label,
    apply: () => {},
    revert: () => {},
  };
}
